import { useState } from 'react';
import { useDraft } from '../contexts/DraftContext';

const POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

// Mirrors DraftControls.jsx's calculateTeamIndex / DraftService.calculateTeamIndex.
function calculateTeamIndex(draftType, teamCount, round, pickNumber) {
  if (draftType?.toLowerCase() === 'snake' && round % 2 === 0) {
    return teamCount - pickNumber;
  }
  return pickNumber - 1;
}

/**
 * Scrollable list of undrafted players sorted by consensus rank.
 * Supports position filter and name search.
 * Clicking a player records their pick: for a classic (non-Commissioner Mode) draft, owner only,
 * as before; for Commissioner Mode, whichever participant (owner/member/guest) occupies the
 * on-clock slot -- the backend's per-slot authorization is the real gate either way, this just
 * keeps the click target from looking pickable when it would only be rejected server-side.
 */
function AvailablePlayers() {
  const { availablePlayers, draft, isOwner, mySlotIndex, recordPick, pickLoading, aiPicking } = useDraft();
  const [posFilter, setPosFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const onClockSlot = draft
    ? calculateTeamIndex(draft.draftType, draft.teamCount, draft.currentRound, draft.currentPick)
    : null;
  const canPick = draft?.status === 'active'
    && (draft?.commissionerMode ? mySlotIndex === onClockSlot : isOwner);

  const filtered = availablePlayers.filter(p => {
    if (posFilter !== 'ALL' && p.position !== posFilter) return false;
    if (search && !p.playerName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  async function handlePick(player) {
    if (!canPick || pickLoading) return;
    await recordPick({
      playerName: player.playerName,
      position: player.position,
      team: player.team,
    });
  }

  return (
    <div className='available-players'>
      <div className='available-header'>
        <h3 className='panel-title'>Available Players</h3>
        <input
          className='input search-input'
          placeholder='Search...'
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className='filter-group' style={{ marginBottom: 8 }}>
        {POSITIONS.map(pos => (
          <button
            key={pos}
            className={`filter-pill ${posFilter === pos ? 'active' : ''}`}
            onClick={() => setPosFilter(pos)}
          >
            {pos}
          </button>
        ))}
      </div>

      {aiPicking && (
        <div className='ai-picking-banner'>
          <span className='spinner' style={{ width: 16, height: 16, borderWidth: 2 }} />
          AI teams are picking&hellip;
        </div>
      )}

      <div className='player-list'>
        {filtered.slice(0, 250).map((p) => (
          <div
            key={p.playerName}
            className={`player-row ${canPick && !pickLoading ? 'clickable' : ''}`}
            onClick={() => handlePick(p)}
            role={canPick ? 'button' : undefined}
            tabIndex={canPick ? 0 : undefined}
            onKeyDown={e => e.key === 'Enter' && handlePick(p)}
          >
            <span className='player-rank'>
              <span>{posFilter !== 'ALL' ? 'ovr:' : ''}{p.avgRank?.toFixed(0) ?? '-'}</span>
              {posFilter !== 'ALL' && p.avgPositionRank != null && (
                <span className='player-pos-rank'>pos:{p.avgPositionRank.toFixed(0)}</span>
              )}
            </span>
            <span className={`pos-badge pos-${p.position}`}>{p.position}</span>
            <span className='player-name'>{p.playerName}</span>
            <span className='player-nfl-team'>{p.team}</span>
          </div>
        ))}
        {filtered.length === 0 && (
          <p style={{ color: 'var(--text-muted)', padding: '12px 8px' }}>No players found.</p>
        )}
      </div>
    </div>
  );
}

export default AvailablePlayers;
